
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2, RefreshCw, Plus, Calendar, CreditCard, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

import { merchantApi } from '../lib/api/merchantApi';
import { Merchant, MerchantProfile } from '../lib/api/types';
import { PartnerSelector } from './PartnerSelector';

interface MerchantListProps {
  onCreateMerchant?: () => void;
}

export const MerchantList: React.FC<MerchantListProps> = ({ onCreateMerchant }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | undefined>();

  // Query to fetch all admin merchants
  const {
    data: merchantData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['merchants-admin', selectedPartnerId],
    queryFn: () => merchantApi.getAllMerchantsAdmin(selectedPartnerId),
  });

  // Query to fetch all merchant profiles
  const {
    data: profilesData,
    isLoading: profilesLoading,
    error: profilesError,
  } = useQuery({
    queryKey: ['merchant-profiles'],
    queryFn: merchantApi.getAllMerchantProfiles,
  });

  if (isLoading || profilesLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Merchants and Profiles...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (error || profilesError) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-destructive">Error Loading Data</CardTitle>
          <CardDescription>
            {error instanceof Error ? error.message : profilesError instanceof Error ? profilesError.message : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const merchants = merchantData?.merchants || [];
  const profiles = profilesData?.merchantProfiles || [];

  // Function to get profiles for a specific merchant by account number
  const getProfilesForMerchant = (accountNumber: string): MerchantProfile[] => {
    return profiles.filter((profile: MerchantProfile) => {
      // Find the account number in the processor data - it's stored as 'accountNum'
      const accountNumberField = profile.processorData.find(
        (field: any) => field.ProcessorField === 'accountNum'
      );
      return accountNumberField?.Value === accountNumber;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Merchants</h2>
          <p className="text-muted-foreground">
            {merchants.length === 0
              ? 'No merchants found'
              : `${merchants.length} merchant${merchants.length === 1 ? '' : 's'}`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {onCreateMerchant && (
            <Button onClick={onCreateMerchant} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create Merchant
            </Button>
          )}
        </div>
      </div>

      {/* Partner Filter */}
      <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Filter by Partner</label>
          <PartnerSelector
            selectedPartnerId={selectedPartnerId}
            onPartnerSelect={(partner) => setSelectedPartnerId(partner?.partnerId)}
            placeholder="All partners"
          />
        </div>
        {selectedPartnerId && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedPartnerId(undefined)}
            className="mt-6"
          >
            Clear Filter
          </Button>
        )}
      </div>

      {/* Merchant list */}
      {merchants.length === 0 ? (
        <Card className="w-full">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 mb-4 rounded-full bg-muted flex items-center justify-center">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Merchants</h3>
              <p className="text-muted-foreground mb-4">
                Get started by creating your first merchant for testing.
              </p>
              {onCreateMerchant && (
                <Button onClick={onCreateMerchant}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Merchant
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {merchants.map((merchant: Merchant) => (
            <MerchantCard
              key={merchant.id}
              merchant={merchant}
              profiles={getProfilesForMerchant(merchant.accountNumber)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface MerchantProfileCardProps {
  profile: MerchantProfile;
}

const MerchantProfileCard: React.FC<MerchantProfileCardProps> = ({ profile }) => {
  const formatProcessorData = (processorData: { ProcessorField: string; Value: string }[]) => {
    return processorData.map((field, index) => (
      <div key={index} className="flex justify-between text-xs">
        <span className="text-muted-foreground">{field.ProcessorField}:</span>
        <span className="font-mono text-xs max-w-32 truncate" title={field.Value}>
          {field.Value}
        </span>
      </div>
    ));
  };

  return (
    <div className="border rounded-lg p-3 bg-muted/30">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h4 className="text-sm font-medium">{profile.profileName}</h4>
          <p className="text-xs text-muted-foreground">ID: {profile.profileId}</p>
        </div>
        <div className="text-xs text-muted-foreground">
          {profile.paymentProcessor}
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Status:</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${
            profile.status === 'Active'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {profile.status}
          </span>
        </div>

        {profile.processorData.length > 0 && (
          <div className="pt-2 border-t">
            <div className="text-xs text-muted-foreground mb-1">Processor Data:</div>
            <div className="space-y-1">
              {formatProcessorData(profile.processorData)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface MerchantCardProps {
  merchant: Merchant;
  profiles: MerchantProfile[];
}

const MerchantCard: React.FC<MerchantCardProps> = ({ merchant, profiles }) => {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const deleteMerchantMutation = useMutation({
    mutationFn: (merchantId: string) => merchantApi.deleteMerchantAdmin(merchantId),
    onSuccess: (response) => {
      toast.success('Merchant deleted successfully', {
        description: `Merchant ID: ${response.merchantId}`,
      });
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
    onError: (error: any) => {
      console.error('Error deleting merchant:', error);
      const errorMessage = error.response?.data?.ErrorMessage || error.message || 'Failed to delete merchant';
      toast.error('Failed to delete merchant', {
        description: errorMessage,
      });
    },
  });

  const handleDelete = () => {
    deleteMerchantMutation.mutate(merchant.id);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {merchant.merchantName}
            </CardTitle>
            <CardDescription>
              ID: {merchant.id}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={deleteMerchantMutation.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Merchant</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete merchant "{merchant.merchantName}"?
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteMerchantMutation.isPending ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid gap-2">
            {merchant.partnerName && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Partner:</span>
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                  {merchant.partnerName}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Account Number:</span>
              <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                {merchant.accountNumber}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Biller ID:</span>
              <span className="font-mono text-xs bg-muted px-2 py-1 rounded">
                {merchant.billerId}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Auth Token:</span>
              <span className="font-mono text-xs bg-muted px-2 py-1 rounded max-w-48 truncate" title={merchant.authToken}>
                {merchant.authToken}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Created:
              </span>
              <span className="text-xs">
                {formatDate(merchant.createdAt)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Updated:
              </span>
              <span className="text-xs">
                {formatDate(merchant.updatedAt)}
              </span>
            </div>
          </div>

          {/* Merchant Profiles Section */}
          <div className="border-t pt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="w-full justify-between p-2 h-auto"
            >
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span className="text-sm font-medium">
                  Merchant Profiles ({profiles.length})
                </span>
              </div>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>

            {expanded && (
              <div className="mt-2 space-y-2">
                {profiles.length === 0 ? (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    No profiles found for this merchant
                  </div>
                ) : (
                  profiles.map((profile) => (
                    <MerchantProfileCard key={profile.profileId} profile={profile} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
